import {
  CLAIM_PROBE_INTERVAL_MS,
  CLAIM_PROBE_ROUNDS,
  CLAIM_PROBE_TIMEOUT_MS,
  PRESENCE_CHANNEL_NAME,
  PRESENCE_PING_INTERVAL_MS,
  PRESENCE_PING_ROUNDS,
  PRESENCE_TIMEOUT_MS
} from "@/store/tabStore/keys";
import { PresenceMessage, PresenceMessageEnum } from "@/store/tabStore/types";

// ── Presence (PING/PONG) ────────────────────────────────────────────────────
// How tabs prove they are alive: this tab answers every PING, and pingLiveTabs()
// asks the others before the cleanup sweep deletes their keys.

/** True when the browser exposes the BroadcastChannel global (Safari <15.4 does not). */
const isBroadcastChannelSupported = (): boolean =>
  typeof globalThis.BroadcastChannel !== "undefined";

/** Module-private, so "one channel per tab" can only be broken from this file. */
let channel: BroadcastChannel | null = null;

/** Close the open channel, if any. */
export const closePresenceChannel = (): void => {
  channel?.close();
  channel = null;
};

/**
 * Open this tab's channel and start answering PINGs.
 *
 * Skipped where the `BroadcastChannel` global doesn't exist (older Safari) — the
 * TS type says nothing about that, so it must be checked at runtime.
 * Calling this twice closes the first channel, never leaves two alive.
 */
export const setupPresenceChannel = (tabId: string): void => {
  if (!isBroadcastChannelSupported()) return;

  closePresenceChannel();
  const presenceChannel = new BroadcastChannel(PRESENCE_CHANNEL_NAME);
  channel = presenceChannel;

  presenceChannel.addEventListener("message", (event) => {
    const msg = event.data as PresenceMessage;
    if (msg?.type === PresenceMessageEnum.PING) {
      presenceChannel.postMessage({
        type: PresenceMessageEnum.PONG,
        requestId: msg.requestId,
        from: tabId
      } satisfies PresenceMessage);
    }
  });
};

/**
 * Is `tabId` still owned by a live tab? True only if a PONG carries that exact id
 * — the duplicate-tab case (source tab still open). On a refresh the inherited id
 * belongs to a page that no longer exists, so nobody answers and this is false.
 *
 * Uses its own short-lived channel and must run BEFORE `setupPresenceChannel`, or
 * this tab would answer its own PING and look like the source tab.
 */
export const isTabIdClaimed = (tabId: string): Promise<boolean> =>
  new Promise((resolve) => {
    if (!isBroadcastChannelSupported()) {
      resolve(false);
      return;
    }

    const probe = new BroadcastChannel(PRESENCE_CHANNEL_NAME);
    // Tagged so PONGs from a sweep elsewhere can't answer for us.
    const requestId = `claim:${tabId}:${Date.now()}`;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let settled = false;

    const finish = (claimed: boolean) => {
      if (settled) return;
      settled = true;
      timers.forEach(clearTimeout);
      probe.close();
      resolve(claimed);
    };

    probe.addEventListener("message", (event) => {
      const msg = event.data as PresenceMessage;
      if (
        msg?.type === PresenceMessageEnum.PONG &&
        msg.requestId === requestId &&
        msg.from === tabId
      ) {
        finish(true); // answered: no need to wait out the window
      }
    });

    // Repeat the PING a few times — a busy tab can miss a single one.
    for (let round = 0; round < CLAIM_PROBE_ROUNDS; round += 1) {
      const send = () => {
        if (settled) return;
        probe.postMessage({
          type: PresenceMessageEnum.PING,
          requestId
        } satisfies PresenceMessage);
      };
      if (round === 0) send();
      else timers.push(setTimeout(send, round * CLAIM_PROBE_INTERVAL_MS));
    }

    // Nobody answered in time: treat the id as free (normal refresh / new tab).
    timers.push(setTimeout(() => finish(false), CLAIM_PROBE_TIMEOUT_MS));
  });

/** Ask other tabs to prove they are alive; resolve with the ids that answered. */
export const pingLiveTabs = (selfId: string): Promise<Set<string>> =>
  new Promise((resolve) => {
    if (!channel) {
      resolve(new Set());
      return;
    }
    const presenceChannel = channel;
    const alive = new Set<string>();
    // Tags this round of PINGs so we ignore PONGs meant for an earlier call.
    const requestId = `${selfId}:${Date.now()}`;

    const onMessage = (event: MessageEvent) => {
      const msg = event.data as PresenceMessage;
      if (
        msg?.type === PresenceMessageEnum.PONG &&
        msg.requestId === requestId &&
        msg.from !== selfId
      ) {
        alive.add(msg.from);
      }
    };
    presenceChannel.addEventListener("message", onMessage);

    // Repeat the PING a few times — a busy tab can miss a single one.
    let round = 0;
    const sendPing = () => {
      presenceChannel.postMessage({
        type: PresenceMessageEnum.PING,
        requestId
      } satisfies PresenceMessage);
      round += 1;
      if (round < PRESENCE_PING_ROUNDS) {
        setTimeout(sendPing, PRESENCE_PING_INTERVAL_MS);
      }
    };
    sendPing();

    // Window closed: stop listening, whoever stayed silent counts as dead.
    setTimeout(() => {
      presenceChannel.removeEventListener("message", onMessage);
      resolve(alive);
    }, PRESENCE_TIMEOUT_MS);
  });
